import { flatten, Indexed, isArray, isObject, isString } from '../utils';
import { CssModule } from './types';

/*
Usage:

import * as global from '../assets/global.scss'; // Global styles
import * as styles from './styles.scss'; // Component styles
import * as modal from './../modal.scss'; // Component styles
const tx = tsClassNames(styles); // Contains now styles from shared and component
const tx = tsClassNames(styles, global); // Contains now styles from shared, global and component
const tx = tsClassNames(styles, global, modal); // Contains now styles from shared, global, component and modal

class={ tx('dates', { modal: this.state.open, icon: this.props.showIcon }) }
class={ tx(externalClassName, 'dates', 'ps-large-b', 'theme-class') } // External, shared and theme classes

const bundle: string[] = ['font-body-1', 'pr-medium'];
class={ tx('dates', bundle, 'theme-class') } // External, array and theme classes
*/

type ClassNameMap = { [className: string]: string };
export type ClassEntry = string | string[] | Indexed<boolean>;
export type ClassMapSource = CssModule;
export interface ClassNames {
  (...classes: ClassEntry[]): string;
}

const resolveClassNames = (classNameMap: ClassNameMap, classes: ClassEntry[] = []): string[] => {
  const resolveClass = (key: string): string[] => key.split(' ').map((part: string) => classNameMap[part] || part);
  // For some reason TypeScript went berzerk with the reduce version when arrays were added
  const resolvedNames: string[] = [];
  classes.forEach((entry: ClassEntry) => {
    if (isString(entry)) {
      resolvedNames.push(...resolveClass(entry));
    } else if (isArray(entry)) {
      resolvedNames.push(...entry.map(resolveClass).reduce(flatten, []));
    } else if (isObject(entry)) {
      resolvedNames.push(
        ...Object.keys(entry)
          .filter((key: string) => entry[key])
          .map(resolveClass)
          .reduce(flatten, [])
      );
    }
  });
  return resolvedNames;
};

export function classNames(...sources: ClassMapSource[]): ClassNames {
  const classNameMap: ClassNameMap = sources.reduce(
    (acc: ClassNameMap, { _api, ...locals }: CssModule): ClassNameMap => ({ ...acc, ...locals }), // eslint-disable-line @typescript-eslint/no-unused-vars
    {}
  );
  return (...classes: ClassEntry[]): string => resolveClassNames(classNameMap, classes).join(' ');
}
